import { useEffect } from "react";
import { X } from "lucide-react";
import WaitlistForm from "./WaitlistForm";

/**
 * WaitlistDialog
 *
 * Centered modal (bottom-sheet on mobile) that hosts the full waitlist
 * form. Mounted by Layout on every non-home route so deep pages can open
 * the form in place instead of navigating back to the homepage.
 *
 * Important: this component only renders the form when `open` is true,
 * so the homepage form remains the single instance of
 * `id="topparent-waitlist-form"` in the DOM whenever the visitor is on /.
 *
 * The form itself is the unmodified WaitlistForm used on the homepage,
 * so KickoffLabs (kol.js) binds and submits exactly as it does there.
 * After submission, KickoffLabs redirects to /thank-you with ?kolid=...
 * which closes the dialog naturally via route change.
 */
const WaitlistDialog = ({ open, onClose }) => {
    // Esc to close, lock body scroll, focus the first input once mounted.
    useEffect(() => {
        if (!open) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const focusTimer = setTimeout(() => {
            const input = document.getElementById("tp-waitlist-name");
            if (input && typeof input.focus === "function") {
                input.focus({ preventScroll: true });
            }
        }, 120);

        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKey);
            clearTimeout(focusTimer);
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            data-testid="waitlist-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Join the TopParent waitlist"
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6 animate-fade-up"
        >
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-brand-navy/65 backdrop-blur-sm"
                data-testid="waitlist-dialog-backdrop"
            />

            <div className="relative w-full max-w-[480px] sm:max-w-[520px] max-h-[94vh] flex flex-col rounded-3xl bg-white shadow-hover ring-1 ring-brand-navy/[0.08] overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 px-6 sm:px-8 pt-6 pb-4 border-b border-brand-navy/[0.06] bg-white">
                    <div className="min-w-0">
                        <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-brand-coral leading-tight">
                            Join the waitlist
                        </p>
                        <h2 className="font-heading text-[20px] sm:text-[22px] font-bold text-brand-navy leading-tight mt-1 tracking-tight">
                            Be among the first to try TopParent.
                        </h2>
                        <p className="mt-1.5 text-[13.5px] text-[#475569] leading-[1.55]">
                            A short chat, and one simple activity at a time, shaped
                            around your child.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="waitlist-dialog-close"
                        className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full text-brand-navy/70 hover:text-brand-navy hover:bg-brand-navy/[0.06] transition-colors"
                    >
                        <X size={18} strokeWidth={2.25} />
                    </button>
                </div>

                {/* Form */}
                <div className="overflow-y-auto px-6 sm:px-8 py-6 bg-brand-offWhite/40">
                    <WaitlistForm />
                    <p className="mt-4 text-[12px] text-[#64748B] leading-[1.55] text-center">
                        We will email launch updates and early access information.
                        Opt out anytime.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default WaitlistDialog;
